import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Check, ChevronDown, Plus, Settings } from "lucide-react";
import { toast } from "sonner";
import { useAuthStore } from "@/stores/auth";
import { createOrganization } from "@/api/client";
import { initialsFor, paletteForOrg } from "@/lib/orgColor";
import { Icon } from "@/components/ds";

/**
 * Org switcher — sets the active organisation (tenant scope). Persists via the
 * auth store; opening reveals the org list with the current one checked.
 * Preserves all legacy behaviour: "All organizations" (multi-org),
 * Manage link, inline New Organization form, navigate on org-page switch.
 * Closes on outside-click and Esc.
 */
export function OrgSwitcher() {
  const navigate = useNavigate();
  const location = useLocation();
  const organizations = useAuthStore((s) => s.organizations);
  const activeOrganizationId = useAuthStore((s) => s.activeOrganizationId);
  const setActiveOrganization = useAuthStore((s) => s.setActiveOrganization);
  const loadOrganizations = useAuthStore((s) => s.loadOrganizations);

  const [open, setOpen] = useState(false);
  const [showNewOrg, setShowNewOrg] = useState(false);
  const [newOrgName, setNewOrgName] = useState("");
  const [creatingOrg, setCreatingOrg] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const activeOrg = organizations.find((o) => o.id === activeOrganizationId);
  const allOrgs = activeOrganizationId === null && organizations.length > 1;
  const label = allOrgs ? "All organizations" : activeOrg?.name || "Select org";
  const avatarPalette = paletteForOrg(activeOrg?.id);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const pickOrg = (id: string | null) => {
    setActiveOrganization(id);
    setOpen(false);
    if (location.pathname.startsWith("/organizations/")) {
      navigate(id ? `/organizations/${id}` : "/festivals");
    }
  };

  const handleCreateOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newOrgName.trim()) return;
    setCreatingOrg(true);
    try {
      const org = await createOrganization(newOrgName.trim());
      await loadOrganizations();
      setActiveOrganization(org.id);
      setNewOrgName("");
      setShowNewOrg(false);
      setOpen(false);
      toast.success("Organization created");
      navigate(`/organizations/${org.id}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create organization");
    } finally {
      setCreatingOrg(false);
    }
  };

  return (
    <div className={`orgwrap ${open ? "orgwrap--open" : ""}`} ref={wrapRef}>
      <button
        type="button"
        data-org-switcher
        className="orgchip"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span
          className="orgchip__avatar"
          style={{ background: allOrgs ? "var(--tr-panel-3)" : avatarPalette.accent } as CSSProperties}
        >
          {allOrgs ? "·" : initialsFor(activeOrg?.name)}
        </span>
        <span className="orgchip__name">{label}</span>
        <span className="orgchip__chev">
          <Icon icon={ChevronDown} size={14} />
        </span>
      </button>

      <div className="orgmenu" role="menu" aria-label="Switch organisation">
        <div className="orgmenu__label">Switch organisation</div>

        {organizations.length > 1 && (
          <button
            type="button"
            role="menuitemradio"
            aria-checked={activeOrganizationId === null}
            className={`orgmenu__item ${activeOrganizationId === null ? "orgmenu__item--on" : ""}`}
            onClick={() => pickOrg(null)}
          >
            <span className="orgmenu__av" style={{ background: "var(--tr-panel-3)" }}>
              ·
            </span>
            <span className="orgmenu__body">
              <div className="orgmenu__name">All organizations</div>
              <div className="orgmenu__plan">Combined view</div>
            </span>
            <span className="orgmenu__check">
              <Icon icon={Check} size={14} />
            </span>
          </button>
        )}

        {organizations.map((org) => {
          const on = org.id === activeOrganizationId;
          const pal = paletteForOrg(org.id);
          return (
            <button
              key={org.id}
              type="button"
              role="menuitemradio"
              aria-checked={on}
              className={`orgmenu__item ${on ? "orgmenu__item--on" : ""}`}
              onClick={() => pickOrg(org.id)}
            >
              <span className="orgmenu__av" style={{ background: pal.accent }}>
                {initialsFor(org.name)}
              </span>
              <span className="orgmenu__body">
                <div className="orgmenu__name">{org.name}</div>
                <div className="orgmenu__plan">{org.is_personal ? "Personal" : "Team"}</div>
              </span>
              <span className="orgmenu__check">
                <Icon icon={Check} size={14} />
              </span>
            </button>
          );
        })}

        <div className="orgmenu__div" />

        <Link
          to={activeOrg ? `/organizations/${activeOrg.id}` : "#"}
          onClick={() => setOpen(false)}
          className="orgmenu__manage"
        >
          <Icon icon={Settings} size={13} />
          Manage organisations
        </Link>

        {showNewOrg ? (
          <form onSubmit={handleCreateOrg} className="mt-1 space-y-1.5 border-t border-line-2 px-1.5 pt-1.5">
            <input
              value={newOrgName}
              onChange={(e) => setNewOrgName(e.target.value)}
              placeholder="Organization name"
              autoFocus
              className="input"
              style={{ fontSize: 12.5, padding: "6px 9px" }}
            />
            <div className="flex gap-1.5">
              <button
                type="submit"
                disabled={creatingOrg || !newOrgName.trim()}
                className="btn btn--accent btn--sm flex-1"
              >
                {creatingOrg ? "Creating…" : "Create"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowNewOrg(false);
                  setNewOrgName("");
                }}
                className="btn btn--ghost btn--sm"
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <button type="button" className="orgmenu__manage" onClick={() => setShowNewOrg(true)}>
            <Icon icon={Plus} size={13} />
            New organisation
          </button>
        )}
      </div>
    </div>
  );
}
